import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import io from "socket.io-client";
import axios from "axios";
import { FormattedMessage, useIntl } from "react-intl";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Layout from "./Layout";
import { useRecaptchaEnterprise } from "../hooks/useRecaptchaEnterprise";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { Button } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import messageToneFile from "../../public/assets/sounds/messageTone.mp3";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RingHierarchy from "./RingHierarchy";

const Qr = () => {
  const intl = useIntl();
  const getGPSLocation = true;
  const [announce, setAnnounce] = useState(false);
  const [messages, setMessages] = useState([]);
  const [ring, setRing] = useState(null);
  const [selectedRing, setSelectedRing] = useState(null);
  const [location, setLocation] = useState(null);
  const [inputMessage, setInputMessage] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const { ready: recaptchaReady, executeRecaptcha: executeRecaptchaEnterprise } = useRecaptchaEnterprise();
  const { qrCode } = useParams();
  const socketRef = useRef();
  const messageTone = new Audio(messageToneFile);
  const acceptedDistance = 300; // metres
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [locationStatus, setLocationStatus] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef(null);
  const executeRecaptchaEnterpriseRef = useRef(executeRecaptchaEnterprise);
  const handleGetLocationRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  const isVerifiedRef = useRef(isVerified);
  const announceRef = useRef(announce);
  const locationStatusRef = useRef(locationStatus);

  useEffect(() => {
    executeRecaptchaEnterpriseRef.current = executeRecaptchaEnterprise;
  }, [executeRecaptchaEnterprise]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    isVerifiedRef.current = isVerified;
  }, [isVerified]);

  useEffect(() => {
    announceRef.current = announce;
  }, [announce]);

  useEffect(() => {
    locationStatusRef.current = locationStatus;
  }, [locationStatus]);

  useEffect(() => {
    handleGetLocationRef.current = handleGetLocation;
  });

  useEffect(() => {
  axios.get(process.env.REACT_APP_API_URL + "/api/position/" + qrCode)
    .then((response) => {
      setRing(response.data);
    })
    .catch((error) => console.error(error));
}, []);

  const executeRecaptcha = async (action) => {
    try {
      setIsVerifying(true);
      const token = await executeRecaptchaEnterpriseRef.current(action);

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/federated/google/verifyRecaptcha`,
        {
          token,
          action,
        }
      );

      setIsVerifying(false);

      if (data.success && data.score > 0.5) {
        setIsVerified(true);
        return true;
      } else {
        console.error("Verificación de reCAPTCHA fallida");
        return false;
      }
    } catch (error) {
      console.error("Error en la verificación de reCAPTCHA:", error);
      setIsVerifying(false);
      return false;
    }
  };

  const handleInitialLocationCheck = async () => {
    if (!isVerifiedRef.current) {
      console.warn("Verificación inicial de reCAPTCHA no completada");
    }
    if (handleGetLocationRef.current) {
      handleGetLocationRef.current();
    }
  };

  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_API_URL, {
      path: "/api/socket.io",
    });
    socketRef.current.on("connect_error", (err) => {
      console.error("Error de conexión:", err);
    });
    socketRef.current.on("connect", () => {
      socketRef.current.emit("start_session", { qrCode: qrCode });
      addMessage(2, intl.formatMessage({ id: "app.qr.visitor.announce" }));
    });
    socketRef.current.on("session_started", (data) => {
      setSessionId(data.sessionId);
      if (getGPSLocation) {
        navigator.permissions.query({ name: "geolocation" }).then((result) => {
          if (result.state === "granted") {
            handleInitialLocationCheck();
          }
        });
      }
    });
    socketRef.current.on("owner_message", (data) => {
      messageTone.play();
      addMessage(2, data.message);
    });

    socketRef.current.on("owner_audio", (data) => {
      const obj = { type: "audio", url: data.audioUrl };
      console.log("data.audioUrl", data);
      addMessage(2, "Recibiste un mensaje de audio", obj);
      // const audio = new Audio(data.audioUrl);
      // audio.play();
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, []);

  // Función para formatear el tiempo de grabación
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const addMessage = (sender, message, obj = {}) => {
    // setMessages((prevMessages) => [...prevMessages, { sender, message, ...obj }]);
    setMessages((prevMessages) => [...prevMessages, { sender, message, obj }]);
  };

  const renderMessage = (sender, message, obj, index) => {
    console.log("obj", obj);
    if (obj.type && obj.type === "audio") {
      return (
        <div key={index} style={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body1" style={{ marginRight: 8 }}>
            Recibiste un mensaje de audio
          </Typography>
          <IconButton
            onClick={() => {
              const audio = new Audio(obj.url);
              audio.play();
            }}
          >
            <PlayCircleIcon />
          </IconButton>
        </div>
      );
    }

    return (
      <ListItem key={index}>
        <ListItemText
          primary={
            <Typography
              sx={{
                bgcolor: sender === 1 ? "#1976d2" : "#dfdfdf",
                color: sender === 1 ? "white" : "black",
                p: 1,
                borderRadius: 2,
                display: "inline-block",
              }}
            >
              {message}
            </Typography>
          }
          sx={{
            textAlign: sender === 1 ? "right" : "left",
          }}
        />
      </ListItem>
    );
  };

  const sendMessage = async () => {
    if (inputMessage && sessionId) {
      // const isVerified = await executeRecaptcha('send_message');
      if (!isVerified) {
        setShowAlert(true);
        setAlertMessage(
          "No se pudo verificar que eres humano. Por favor, intenta de nuevo."
        );
        return;
      }

      if (locationStatus === 1) {
        socketRef.current.emit("visitor_message", {
          message: inputMessage,
          qrCode: qrCode,
          sessionId: sessionId,
        });
        addMessage(1, inputMessage);
        setInputMessage("");
      } else {
        setShowAlert(true);
        setAlertMessage("No se pudo verificar que te encuentres cerca del QR.");
        return;
      }
    }
  };

  const handleGetLocation = async () => {
    const isVerified = await executeRecaptcha("get_location");
    if (!isVerified) {
      console.warn("Verificación de ubicación no completada");
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        axios
          .post(process.env.REACT_APP_API_URL + "/api/position/" + qrCode, {
            lat: latitude,
            lng: longitude,
          })
          .then((response) => {
            setLocationStatus(response.data.status);
          })
          .catch((error) => {
            setLocationStatus(0);
            console.log(error);
          });
        if (announce && locationStatus === 1) {
          socketRef.current.emit("visitor_message", {
            message: "Timbre",
            qrCode: qrCode,
            sessionId: sessionId,
          });
        }
      },
      (error) => {
        console.error("Error obteniendo la ubicación:", error);
      }
    );
  };

  const sendAudioMessage = () => {
    if (audioBlob) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Audio = reader.result.split(",")[1];
        socketRef.current.emit("visitor_audio", {
          audio: base64Audio,
          qrCode,
          sessionId,
        });
        addMessage(1, "Audio enviado");
        setAudioBlob(null);
      };
      reader.readAsDataURL(audioBlob);
    }
  };

  const hideAlert = () => {
    setShowAlert(false);
    setAlertMessage(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        audioChunksRef.current = [];
        clearInterval(recordingTimerRef.current);
        setRecordingTime(0);
        // Detener todas las pistas del stream de audio
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);

      // Iniciar el temporizador
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error al acceder al micrófono:", error);
      setShowAlert(true);
      setAlertMessage(
        "No se pudo acceder al micrófono. Por favor, verifica los permisos."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
      setIsRecording(false);
      setAudioBlob(null);
      audioChunksRef.current = [];
      clearInterval(recordingTimerRef.current);
      setRecordingTime(0);
    }
  };

  // Componente para el input
  const renderInputArea = () => (
    <Box sx={{ p: 2, bgcolor: "background.paper" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isRecording ? (
          // Estado de grabación
          <>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                p: 1,
              }}
            >
              <FiberManualRecordIcon
                sx={{
                  color: "red",
                  animation: "pulse 1.5s infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.5 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
              <Typography sx={{ ml: 1 }}>
                Grabando... {formatTime(recordingTime)}
              </Typography>
            </Box>
            <IconButton color="error" onClick={cancelRecording}>
              <CancelIcon />
            </IconButton>
            <IconButton color="primary" onClick={stopRecording}>
              <CheckIcon />
            </IconButton>
          </>
        ) : (
          // Estado normal
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Escribe un mensaje..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              size="small"
            />
            {inputMessage ? (
              <IconButton
                color="primary"
                onClick={sendMessage}
                disabled={isVerifying}
              >
                <SendIcon />
              </IconButton>
            ) : (
              <IconButton
                color="primary"
                onClick={startRecording}
                disabled={isVerifying}
              >
                <MicIcon />
              </IconButton>
            )}
          </>
        )}

        {audioBlob && !isRecording && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              p: 1,
            }}
          >
            <IconButton
              color="error"
              size="small"
              onClick={() => setAudioBlob(null)}
            >
              <CancelIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                const audio = new Audio(URL.createObjectURL(audioBlob));
                audio.play();
              }}
            >
              <PlayCircleIcon />
            </IconButton>
            <IconButton color="primary" size="small" onClick={sendAudioMessage}>
              <SendIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Layout>
      <Collapse in={showAlert}>
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={hideAlert}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          <p>{alertMessage}</p>
        </Alert>
      </Collapse>

      <Container maxWidth={false} disableGutters>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} sx={{ textAlign: "center" }}>
            {!location ? (
              <div>
                <div>
                  Necesitamos obtener tu posición para confirmar que te
                  encuentras enfrente del timbre
                </div>
                <Button
                  onClick={handleGetLocation}
                  disabled={isVerifying}
                  variant="outlined"
                  color="primary"
                >
                  {isVerifying ? "Verificando..." : "Obtener posición"}
                </Button>
              </div>
            ) : (
              <div id="messagesWrapper">
                <Box sx={{ width: "100%", maxWidth: 600, margin: "0 auto" }}>
                {ring && ring.children && ring.children.length > 0 && (
                  <RingHierarchy
                    ring={ring}
                    onSelect={setSelectedRing}
                    selectedLeaf={selectedRing}
                    onReset={() => setSelectedRing(null)}
                  />
                )}
                  {(!ring || !ring.children || ring.children.length === 0 || selectedRing) && (
                  <Paper elevation={3}>
                    {/* Área de input */}
                    <Box sx={{ p: 2, bgcolor: "background.paper" }}>
                      {renderInputArea()}
                    </Box>
                    {/* Área de mensajes */}
                    <Box
                      sx={{
                        height: 400,
                        overflow: "auto",
                        p: 2,
                        bgcolor: "#f5f5f5",
                      }}
                    >
                      <List>
                        {messages.map((msg, index) =>
                          renderMessage(msg.sender, msg.message, msg.obj, index)
                        )}
                      </List>
                    </Box>
                  </Paper>
                  )}
                </Box>
              </div>
            )}
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default Qr;